import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'
import { ApiError, type components } from '@akillidernek/api-client'
import {
  Badge,
  Button,
  Card,
  CardBody,
  Dialog,
  EmptyState,
  Field,
  Icon,
  Input,
  cn,
  useToast,
} from '@akillidernek/ui'
import { api } from '../api'
import { useTenantSlug } from '../use-tenant'

type Ticket = components['schemas']['TicketRead']
type Message = components['schemas']['MessageRead']
type Status = Ticket['status']

const DURUM: Record<Status, { label: string; tone: 'info' | 'warning' | 'success' | 'neutral' }> = {
  open: { label: 'Açık', tone: 'info' },
  waiting_requester: { label: 'Yanıt bekliyor', tone: 'warning' },
  resolved: { label: 'Çözüldü', tone: 'success' },
  closed: { label: 'Kapalı', tone: 'neutral' },
}

const ONCELIK: Record<Ticket['priority'], string> = {
  low: 'Düşük',
  normal: 'Normal',
  high: 'Yüksek',
  urgent: 'Acil',
}

/** SLA (YES-168). `met`/`on_track` sessiz: rozet yalnızca dikkat isteyince. */
const SLA: Record<
  Ticket['sla_status'],
  { label: string; tone: 'warning' | 'danger' | 'neutral' } | null
> = {
  met: null,
  on_track: null,
  at_risk: { label: 'Süre daralıyor', tone: 'warning' },
  breached: { label: 'Gecikti', tone: 'danger' },
  paused: { label: 'Yanıt bekleniyor', tone: 'neutral' },
}

type Sekme = 'uye' | 'platform'

/**
 * Destek — YES-114.
 *
 * İki kapsam tek ekranda, sekmeyle: derneğin ÜYELERİNDEN gelen talepler ve
 * derneğin PLATFORMA açtıkları. Aynı model olduğu için liste ve mesaj akışı
 * ortak; fark yalnızca kimin talep sahibi olduğunda ve bu da iç notların
 * görünürlüğünü belirliyor.
 */
export function Destek() {
  const [sekme, setSekme] = useState<Sekme>('uye')

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-lg font-semibold">Destek</h1>

      <div
        role="tablist"
        aria-label="Talep türü"
        className="flex gap-1 rounded-[var(--radius-control)] bg-[var(--ad-surface-muted)] p-1"
      >
        {(
          [
            ['uye', 'Üye talepleri'],
            ['platform', 'Platform desteği'],
          ] as const
        ).map(([deger, etiket]) => (
          <button
            key={deger}
            role="tab"
            aria-selected={sekme === deger}
            onClick={() => setSekme(deger)}
            className={cn(
              'rounded-[var(--radius-control)] px-3 py-1.5 text-sm',
              sekme === deger
                ? 'bg-[var(--ad-surface)] font-medium shadow-sm'
                : 'text-[var(--ad-text-muted)]',
            )}
          >
            {etiket}
          </button>
        ))}
      </div>

      {sekme === 'uye' ? <UyeTalepleri /> : <PlatformTalepleri />}
    </div>
  )
}

// --- üye → dernek ------------------------------------------------------------

function UyeTalepleri() {
  const slug = useTenantSlug()
  const [durum, setDurum] = useState<Status | ''>('')
  const [gecikenler, setGecikenler] = useState(false)
  const [secili, setSecili] = useState<Ticket | null>(null)

  const liste = useQuery({
    queryKey: ['tickets', slug, durum, gecikenler],
    enabled: Boolean(slug),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/support/tickets', {
        params: { query: { ticket_status: durum || undefined, overdue: gecikenler || undefined } },
      })
      return data ?? []
    },
  })

  if (liste.isError) {
    return <YetkiHatasi hata={liste.error} ne="Üye talepleri" />
  }

  return (
    <>
      <Card>
        <CardBody className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-4">
            <DurumSuzgeci deger={durum} onDeger={setDurum} />
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={gecikenler}
                onChange={(event) => setGecikenler(event.target.checked)}
              />
              Yalnızca gecikenler
            </label>
          </div>
          <TalepListesi
            talepler={liste.data ?? []}
            yukleniyor={liste.isLoading}
            onSec={setSecili}
            bosBaslik={gecikenler ? 'Geciken talep yok' : 'Üyelerden gelen talep yok'}
            bosAciklama={
              gecikenler
                ? 'Bütün açık talepler SLA hedefinin içinde.'
                : 'Üye portalından açılan talepler burada görünür.'
            }
          />
        </CardBody>
      </Card>

      {secili && (
        <TalepDetay talep={secili} onKapat={() => setSecili(null)} kapsam="uye" slug={slug} />
      )}
    </>
  )
}

// --- dernek → platform -------------------------------------------------------

function PlatformTalepleri() {
  const slug = useTenantSlug()
  const [secili, setSecili] = useState<Ticket | null>(null)
  const [yeniAcik, setYeniAcik] = useState(false)

  const liste = useQuery({
    queryKey: ['platform-tickets', slug],
    enabled: Boolean(slug),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/support/platform-tickets')
      return data ?? []
    },
  })

  if (liste.isError) {
    return <YetkiHatasi hata={liste.error} ne="Platform talepleri" />
  }

  return (
    <>
      <Card>
        <CardBody className="flex flex-col gap-4">
          <div className="flex justify-end">
            <Button onClick={() => setYeniAcik(true)}>
              <Icon name="plus" size={16} aria-hidden />
              Talep aç
            </Button>
          </div>
          <TalepListesi
            talepler={liste.data ?? []}
            yukleniyor={liste.isLoading}
            onSec={setSecili}
            bosBaslik="Açılmış talep yok"
            bosAciklama="Platformla ilgili bir sorun ya da sorunuz olduğunda buradan yazın."
          />
        </CardBody>
      </Card>

      <YeniPlatformTalebi acik={yeniAcik} onAcik={setYeniAcik} slug={slug} />
      {secili && (
        <TalepDetay talep={secili} onKapat={() => setSecili(null)} kapsam="platform" slug={slug} />
      )}
    </>
  )
}

// --- ortak parçalar ----------------------------------------------------------

function DurumSuzgeci({
  deger,
  onDeger,
}: {
  deger: Status | ''
  onDeger: (deger: Status | '') => void
}) {
  return (
    <select
      aria-label="Duruma göre süz"
      className="h-10 max-w-xs rounded-[var(--radius-control)] border border-[var(--ad-border)] bg-[var(--ad-surface)] px-3 text-sm"
      value={deger}
      onChange={(event) => onDeger(event.target.value as Status | '')}
    >
      <option value="">Tüm durumlar</option>
      {Object.entries(DURUM).map(([value, { label }]) => (
        <option key={value} value={value}>
          {label}
        </option>
      ))}
    </select>
  )
}

function TalepListesi({
  talepler,
  yukleniyor,
  onSec,
  bosBaslik,
  bosAciklama,
}: {
  talepler: Ticket[]
  yukleniyor: boolean
  onSec: (talep: Ticket) => void
  bosBaslik: string
  bosAciklama: string
}) {
  if (yukleniyor) {
    return <p className="py-8 text-center text-sm text-[var(--ad-text-muted)]">Yükleniyor…</p>
  }

  if (talepler.length === 0) {
    return (
      <EmptyState
        icon={<Icon name="life-buoy" size={24} aria-hidden />}
        title={bosBaslik}
        description={bosAciklama}
      />
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[var(--ad-border)] text-left text-xs text-[var(--ad-text-muted)]">
            <th className="py-2 pr-3 font-medium">No</th>
            <th className="py-2 pr-3 font-medium">Konu</th>
            <th className="py-2 pr-3 font-medium">Durum</th>
            <th className="py-2 pr-3 font-medium">Öncelik</th>
            <th className="py-2 pr-3 font-medium">SLA</th>
            <th className="py-2 pr-3 font-medium">Son hareket</th>
          </tr>
        </thead>
        <tbody>
          {talepler.map((talep) => (
            <tr
              key={talep.number}
              onClick={() => onSec(talep)}
              className="cursor-pointer border-b border-[var(--ad-border)] last:border-0 hover:bg-[var(--ad-surface-muted)]"
            >
              <td className="py-2 pr-3 tabular-nums text-[var(--ad-text-muted)]">
                #{talep.number}
              </td>
              <td className="py-2 pr-3 font-medium">{talep.subject}</td>
              <td className="py-2 pr-3">
                <Badge tone={DURUM[talep.status].tone}>{DURUM[talep.status].label}</Badge>
              </td>
              <td className="py-2 pr-3">{ONCELIK[talep.priority]}</td>
              <td className="py-2 pr-3">
                {SLA[talep.sla_status] ? (
                  <Badge tone={SLA[talep.sla_status]!.tone}>{SLA[talep.sla_status]!.label}</Badge>
                ) : (
                  <span className="text-xs text-[var(--ad-text-muted)]">—</span>
                )}
              </td>
              <td className="py-2 pr-3 text-[var(--ad-text-muted)]">
                {tarih(talep.last_message_at ?? talep.created_at)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function tarih(iso: string): string {
  return new Date(iso).toLocaleString('tr', { dateStyle: 'short', timeStyle: 'short' })
}

/**
 * Talep akışı ve yanıt kutusu.
 *
 * `kapsam` yalnızca hangi uca gidileceğini ve iç not yazılıp yazılamayacağını
 * belirliyor: platform talebinde DERNEK talep sahibi, yani iç not ona ait bir
 * kavram değil ve sunucu zaten `is_internal` gönderilse bile yok sayıyor.
 */
function TalepDetay({
  talep,
  onKapat,
  kapsam,
  slug,
}: {
  talep: Ticket
  onKapat: () => void
  kapsam: Sekme
  slug: string | null
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [icNot, setIcNot] = useState(false)
  const anahtar = kapsam === 'uye' ? 'tickets' : 'platform-tickets'

  const mesajlar = useQuery({
    queryKey: [anahtar, slug, talep.number, 'messages'],
    queryFn: async () => {
      const { data } =
        kapsam === 'uye'
          ? await api.GET('/api/v1/support/tickets/{number}', {
              params: { path: { number: talep.number } },
            })
          : await api.GET('/api/v1/support/platform-tickets/{number}', {
              params: { path: { number: talep.number } },
            })
      return data ?? []
    },
  })

  const tazele = () => {
    void queryClient.invalidateQueries({ queryKey: [anahtar, slug] })
  }

  const yanitla = useMutation({
    mutationFn: async (govde: string) => {
      if (kapsam === 'uye') {
        await api.POST('/api/v1/support/tickets/{number}/messages', {
          params: { path: { number: talep.number } },
          body: { body: govde, is_internal: icNot },
        })
        return
      }
      await api.POST('/api/v1/support/platform-tickets/{number}/messages', {
        params: { path: { number: talep.number } },
        body: { body: govde, is_internal: false },
      })
    },
    onSuccess: () => {
      setIcNot(false)
      tazele()
    },
    onError: (error) =>
      toast({
        title: 'Mesaj gönderilemedi',
        description: error instanceof ApiError ? error.problem.detail : undefined,
        tone: 'error',
      }),
  })

  const durumDegis = useMutation({
    mutationFn: async (hedef: Status) => {
      await api.POST('/api/v1/support/tickets/{number}/status', {
        params: { path: { number: talep.number } },
        body: { status: hedef },
      })
    },
    onSuccess: () => {
      toast({ title: 'Talep durumu güncellendi', tone: 'success' })
      tazele()
      onKapat()
    },
    onError: (error) =>
      toast({
        title: 'Durum değiştirilemedi',
        description: error instanceof ApiError ? error.problem.detail : undefined,
        tone: 'error',
      }),
  })

  return (
    <Dialog
      open
      onOpenChange={(acik) => !acik && onKapat()}
      title={talep.subject}
      description={`#${talep.number} · ${DURUM[talep.status].label} · ${ONCELIK[talep.priority]}`}
    >
      <div className="flex flex-col gap-4">
        <div className="flex max-h-80 flex-col gap-3 overflow-y-auto">
          {mesajlar.isLoading && <p className="text-sm text-[var(--ad-text-muted)]">Yükleniyor…</p>}
          {(mesajlar.data ?? []).map((mesaj) => (
            <Mesaj key={mesaj.id} mesaj={mesaj} />
          ))}
        </div>

        <form
          className="flex flex-col gap-2 border-t border-[var(--ad-border)] pt-3"
          onSubmit={(event) => {
            event.preventDefault()
            // Form ELEMANI burada yakalanıyor: `event.currentTarget` olay
            // işleyicisi döndükten sonra null olur ve async `onSuccess`
            // içinde okunamaz — kutu temizlenmeden kalırdı.
            const form = event.currentTarget
            const govde = String(new FormData(form).get('body') ?? '').trim()
            if (govde) {
              yanitla.mutate(govde, { onSuccess: () => form.reset() })
            }
          }}
        >
          <textarea
            name="body"
            rows={3}
            required
            placeholder={icNot ? 'Yalnızca görevlilerin göreceği not…' : 'Yanıtınız…'}
            className={cn(
              'w-full rounded-[var(--radius-control)] border bg-[var(--ad-surface)] px-3 py-2 text-sm outline-none',
              'focus-visible:ring-3 focus-visible:ring-[var(--ad-ring)]',
              icNot
                ? 'border-[var(--ad-warning)] bg-[var(--ad-warning)]/8'
                : 'border-[var(--ad-border)]',
            )}
          />

          <div className="flex flex-wrap items-center gap-2">
            {kapsam === 'uye' && (
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={icNot}
                  onChange={(event) => setIcNot(event.target.checked)}
                />
                İç not — talep sahibi görmez
              </label>
            )}
            <Button type="submit" size="sm" className="ml-auto" disabled={yanitla.isPending}>
              {yanitla.isPending ? 'Gönderiliyor…' : icNot ? 'Notu kaydet' : 'Yanıtla'}
            </Button>
          </div>
        </form>

        {kapsam === 'uye' && talep.status !== 'closed' && (
          <div className="flex flex-wrap justify-end gap-2 border-t border-[var(--ad-border)] pt-3">
            {talep.status !== 'resolved' && (
              <Button
                variant="secondary"
                size="sm"
                disabled={durumDegis.isPending}
                onClick={() => durumDegis.mutate('resolved')}
              >
                Çözüldü olarak işaretle
              </Button>
            )}
            <Button
              variant="secondary"
              size="sm"
              disabled={durumDegis.isPending}
              onClick={() => durumDegis.mutate('closed')}
            >
              Kapat
            </Button>
          </div>
        )}
      </div>
    </Dialog>
  )
}

/**
 * Tek mesaj.
 *
 * İç not GÖRSEL OLARAK ayrılıyor: aynı akışta duran, talep sahibine gitmeyen
 * bir metnin sıradan bir yanıt gibi görünmesi, görevlinin yanlışlıkla "zaten
 * yazmıştım" diye düşünmesine yol açar.
 */
function Mesaj({ mesaj }: { mesaj: Message }) {
  return (
    <div
      className={cn(
        'rounded-[var(--radius-control)] px-3 py-2 text-sm',
        mesaj.is_internal
          ? 'border border-dashed border-[var(--ad-warning)] bg-[var(--ad-warning)]/8'
          : mesaj.from_requester
            ? 'bg-[var(--ad-surface-muted)]'
            : 'bg-[var(--ad-primary)]/10',
      )}
    >
      <div className="mb-1 flex items-center gap-2 text-xs text-[var(--ad-text-muted)]">
        <span>{mesaj.from_requester ? 'Talep sahibi' : 'Görevli'}</span>
        {mesaj.is_internal && <Badge tone="warning">İç not</Badge>}
        <span className="ml-auto">{tarih(mesaj.created_at)}</span>
      </div>
      <p className="whitespace-pre-wrap">{mesaj.body}</p>
    </div>
  )
}

function YeniPlatformTalebi({
  acik,
  onAcik,
  slug,
}: {
  acik: boolean
  onAcik: (acik: boolean) => void
  slug: string | null
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [hatalar, setHatalar] = useState<Record<string, string>>({})

  const kaydet = useMutation({
    mutationFn: async (form: FormData) => {
      await api.POST('/api/v1/support/platform-tickets', {
        body: {
          subject: String(form.get('subject')),
          body: String(form.get('body')),
          priority: form.get('priority') as Ticket['priority'],
        },
      })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['platform-tickets', slug] })
      onAcik(false)
      setHatalar({})
      toast({ title: 'Talebiniz açıldı', tone: 'success' })
    },
    onError: (error) => {
      if (error instanceof ApiError) setHatalar(error.fieldErrors())
      toast({
        title: 'Talep açılamadı',
        description: error instanceof ApiError ? error.problem.detail : undefined,
        tone: 'error',
      })
    },
  })

  return (
    <Dialog
      open={acik}
      onOpenChange={onAcik}
      title="Platforma talep aç"
      description="Akıllı Dernek ekibine ulaşır; üyeleriniz bu talebi görmez."
    >
      <form
        className="flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault()
          kaydet.mutate(new FormData(event.currentTarget))
        }}
      >
        <Field label="Konu" required error={hatalar.subject}>
          {(props) => <Input name="subject" autoFocus {...props} />}
        </Field>

        <Field label="Öncelik" error={hatalar.priority}>
          {(props) => (
            <select
              name="priority"
              defaultValue="normal"
              className="h-10 w-full rounded-[var(--radius-control)] border border-[var(--ad-border)] bg-[var(--ad-surface)] px-3 text-sm"
              {...props}
            >
              {Object.entries(ONCELIK).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          )}
        </Field>

        <Field label="Açıklama" required error={hatalar.body}>
          {(props) => (
            <textarea
              name="body"
              rows={5}
              className="w-full rounded-[var(--radius-control)] border border-[var(--ad-border)] bg-[var(--ad-surface)] px-3 py-2 text-sm outline-none focus-visible:ring-3 focus-visible:ring-[var(--ad-ring)]"
              {...props}
            />
          )}
        </Field>

        <div className="mt-1 flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={() => onAcik(false)}>
            Vazgeç
          </Button>
          <Button type="submit" disabled={kaydet.isPending}>
            {kaydet.isPending ? 'Gönderiliyor…' : 'Gönder'}
          </Button>
        </div>
      </form>
    </Dialog>
  )
}

/**
 * Yetki/yükleme hatası.
 *
 * Menüdeki "Destek" izinsiz açılıyor ama iki listenin AYRI izinleri var
 * (`communications.read` ve `settings.read`). Bir sekmeye erişemeyen kullanıcı
 * sonsuza kadar dönen bir ekran değil, sebebini görmeli — Ödeme Ayarları'nda
 * tam olarak bu hatayı yapmıştık.
 */
function YetkiHatasi({ hata, ne }: { hata: unknown; ne: ReactNode }) {
  const yetkisiz = hata instanceof ApiError && hata.problem.status === 403
  return (
    <Card>
      <CardBody>
        <EmptyState
          icon={<Icon name="shield-alert" size={24} aria-hidden />}
          title={yetkisiz ? 'Bu listeyi görme yetkiniz yok' : 'Liste yüklenemedi'}
          description={
            yetkisiz
              ? `${ne} için ayrı bir yetki gerekiyor; dernek yöneticinizden isteyebilirsiniz.`
              : 'Bağlantınızı kontrol edip tekrar deneyin.'
          }
        />
      </CardBody>
    </Card>
  )
}
